import React, {useCallback, useEffect, useMemo, useState} from "react";
import {fetchSelectOptionsByContractNO} from "../../../../backendInteraction/ApiFetch";
import {Button, Input, message, Select, Space, Table} from "antd";

import {ColumnsType} from "antd/es/table";
import {RigMovesData} from "../../../../types/RigMovesData";
import {
    addRigMoveRow,
    deleteRigMoveByRmId,
    fetchRigMoveByPid,
    updateRigMoveRow
} from "../../../../backendInteraction/RigMovesApiFetch";

interface RigMovesTableProps {
    data: RigMovesData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
    holeId: string;
    setHoleId: (value: string) => void;
}

const RigMovesTable: React.FC<RigMovesTableProps> = ({ data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow, holeId, setHoleId }) => {
    const [rigMovesData, setRigMovesData] = useState<RigMovesData[]>([]);
    const [itemOptions, setItemOptions] = useState<{ value: string; label: string }[]>([]);
    const [editingRmId, setEditingRmId] = useState<number | null>(null);
    const [editData, setEditData] = useState<RigMovesData | null>(null);

    const loadRigMovesType = useCallback(async () => {
        if (contractNo) {
            const items = await fetchSelectOptionsByContractNO(contractNo, "RigMoves");
            setItemOptions(items.map(item => ({ value: item, label: item })));
        } else {
            setItemOptions([]);
        }
    }, [contractNo]);

    useEffect(() => {
        loadRigMovesType();
    }, [loadRigMovesType]);

    useEffect(() => {
        setRigMovesData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingRmId !== -1) {
            const len = rigMovesData.length;
            const newRow: RigMovesData = {
                key: -1,
                rm_id: -1,
                hole_id: len > 0 ? rigMovesData[len - 1].hole_id || holeId : holeId,
                move_type: "",
                pid: plodId ?? -1,
            };
            setRigMovesData(prevData => {
                const hasNewRow = prevData.some(item => item.rm_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            });

            setEditingRmId(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingRmId]);

    useEffect(() => {
        if (isView) {
            if (editingRmId === -1) {
                setRigMovesData(prevData => prevData.filter(item => item.rm_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }
            setEditingRmId(null);
            setEditData(null);
        }
    }, [isView]);

    const saveChanges = useCallback(async () => {
        console.log("Saving changes:", editData);

        if (!editData) return;

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingRmId === -1) {
            const success = await addRigMoveRow(editData, aid);
            if (success) {
                setRigMovesData(await fetchRigMoveByPid(plodId ?? -1, aid));
                message.success("New rig moves added successfully!");
            } else {
                message.error("Failed to add new rig moves.");
            }
        } else {
            const success = await updateRigMoveRow(editingRmId ?? -1, editData);
            if (success) {
                setRigMovesData(await fetchRigMoveByPid(plodId ?? -1, aid));
                message.success("Rig moves updated successfully!");
            } else {
                message.error("Failed to update rig moves.");
            }
        }

        !(editData.hole_id === holeId) && setHoleId(editData.hole_id);

        if (editingRmId === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingRmId(null);
        setEditData(null);
    }, [editData, editingRmId, setIsAddNewRow]);

    const cancelEditing = useCallback((record: RigMovesData) => {
        console.log("Cancel editing");
        setEditingRmId(null);
        setEditData(null);

        if (record.rm_id === -1) {
            setRigMovesData(prevData => prevData.filter(item => item.rm_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: RigMovesData) => {
        console.log("Editing row:", record);

        if (editingRmId === -1) {
            setRigMovesData(prevData => prevData.filter(item => item.rm_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingRmId(prevId => {
            if (prevId !== null) cancelEditing({ rm_id: prevId } as RigMovesData);
            return record.rm_id;
        });

        setEditData({ ...record });
    }, [editingRmId, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: RigMovesData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteRigMoveByRmId(record.rm_id);
        if (success) {
            setRigMovesData(await fetchRigMoveByPid(plodId ?? -1, aid));
            message.success("Rig moves deleted successfully!");
        } else {
            message.error("Failed to delete rig moves.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof RigMovesData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);

    const columns: ColumnsType<RigMovesData> = useMemo(() => [
        {
            title: "Hole ID",
            dataIndex: "hole_id",
            key: "hole_id",
            width: "20%",
            render: (_, record) =>
                editingRmId === record.rm_id ? (
                    <Input
                        value={editData?.hole_id}
                        onChange={(e) => handleInputChange("hole_id", e.target.value)}
                    />
                ) : (
                    record.hole_id
                ),
        },
        {
            title: "Rig Moves Type",
            dataIndex: "move_type",
            key: "move_type",
            width: "50%",
            minWidth: 280,
            render: (_, record) =>
                editingRmId === record.rm_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={itemOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.move_type || ""}
                        onChange={(value) => handleInputChange("move_type", value || "")}
                    />
                ) : (
                    record.move_type
                ),
        },
        ...(!isView
            ? [
                {
                    title: "Action",
                    key: "action",
                    width: "30%",
                    render: (_, record) => (
                        <Space size="small">
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingRmId === record.rm_id ? saveChanges() : startEditing(record))}>
                                {editingRmId === record.rm_id ? "Save" : "Edit"}
                            </Button>
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingRmId === record.rm_id ? cancelEditing(record) : deleteRow(record))}>
                                {editingRmId === record.rm_id ? "Cancel" : "Delete"}
                            </Button>
                        </Space>
                    ),
                },
            ] as ColumnsType<RigMovesData>
            : []),
    ], [isView, editingRmId, itemOptions, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<RigMovesData>
            columns={columns}
            pagination={false}
            dataSource={rigMovesData}
        />
    );
};

export default RigMovesTable;